// Org context — resolves the logged-in user's organization from the
// organization_memberships table once, on mount, and makes it available app-wide
// via useOrg(). Also exposes the caller's Floxir platform role: isSuperAdmin (full
// read+write, any company — the admin-only "create customer"/costs surfaces) and
// isPlatformStaff (support OR super_admin — read-only across every company).
//
// can('<permission key>') answers "may I do this here?" for the active company. It is
// loaded from the backend (GET /api/org/permissions), which asks the same database
// function that enforces the rules — so a button is shown exactly when the action
// will be allowed. It FAILS CLOSED: until the answer arrives, or if it can't be
// fetched, can() is false and no change buttons appear. Platform support therefore
// sees no change buttons anywhere (its permissions are read-only ones).
//
// Platform staff hold NO membership row in any customer org (removed along with
// is_super_admin's auto-membership grant) — their entry in `orgs` below comes from
// /api/platform/orgs (every company) instead of organization_memberships, with a
// synthetic role label rather than a real membership role.
//
// START-UP: everything above (companies, active company, permissions, capabilities) is first asked for in
// ONE request, GET /api/session/bootstrap, whose answer is decided by the same backend rules as every other
// request (see server/session_bootstrap.js). If that request fails for any reason other than the staff
// access refusal (for example during a deploy where the website is newer than the backend), the separate
// requests below run exactly as they always did, so nothing depends on the new endpoint being there.
//
// Provider order matters: OrgProvider must wrap any provider/page that needs the
// org (e.g. DraftPOProvider). It gates rendering behind a brief loading screen so
// children can assume orgId is resolved — and shows a clear "no workspace" screen
// (rather than leaking another org's data) if the user has no membership and no
// platform role.

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '../supabase';
import { ORG_STORAGE_KEY, STAFF_ACCESS_BLOCKED_EVENT, apiFetch, isStaffAccessRefusal } from '../lib/api';
import { C } from '../theme';
import { clearAll } from '../lib/dataCache';

export type OrgSummary = {
  currency?: string; id: string; name: string | null; slug: string | null; role: string };
export type Capabilities = { reconciliation: boolean; agent: boolean; manufacturing: boolean };

// Permissions that did not arrive are asked for again a few times. The start-up answer fails CLOSED (no permissions = no change buttons), but a single failed
// lookup (a cold backend, a blip during a deploy) must not leave the buttons off for the whole session. A member of a company always holds at least "view", so an
// EMPTY list means "the lookup did not work". After the last try the list stays empty: still fail closed. The waits are in milliseconds.
export const PERMISSION_RETRY_DELAYS_MS = [700, 2000, 5000];

// Default before the backend answers / for orgs with no config: reconciliation only
// (the pre-existing behaviour, so nothing regresses while capabilities load).
const DEFAULT_CAPS: Capabilities = { reconciliation: true, agent: false, manufacturing: false };

type OrgState = {
  orgId: string | null;
  role: string | null;
  /** Full read+write across every company. Gates the admin-only surfaces
   *  (create-customer, costs console). */
  isSuperAdmin: boolean;
  /** support OR super_admin — read-only-or-better across every company. Gates
   *  nothing admin-only by itself; use isSuperAdmin for that. */
  isPlatformStaff: boolean;
  /** Permission keys the caller holds in the active company (see can()). */
  permissions: string[];
  loading: boolean;
  orgs: OrgSummary[];
  capabilities: Capabilities;
  /** The active org's base currency (ISO code). Every money string the UI renders
   *  should use this instead of a hardcoded symbol — a USD customer seeing "€" on
   *  dollar figures is the kind of detail that costs trust in a money product. */
  currency: string;
};

type OrgContextValue = OrgState & { setOrgId: (id: string) => void; can: (key: string) => boolean };

const OrgContext = createContext<OrgContextValue>({
  orgId: null,
  role: null,
  isSuperAdmin: false,
  isPlatformStaff: false,
  permissions: [],
  loading: true,
  orgs: [],
  capabilities: DEFAULT_CAPS,
  currency: 'EUR',
  setOrgId: () => {},
  can: () => false,
});

export function useOrg() {
  return useContext(OrgContext);
}

// Ask the backend for the whole start-up picture in one request. Never throws.
//   { kind: 'ok', data }      the answer, checked for the shape this file relies on
//   { kind: 'blocked', message }   a staff visit that could not be logged (fail closed: show the message)
//   { kind: 'fallback' }      anything else: use the separate requests
async function loadBootstrap(): Promise<
  | { kind: 'ok'; data: any }
  | { kind: 'blocked'; message?: string }
  | { kind: 'fallback' }
> {
  try {
    const res = await apiFetch('/api/session/bootstrap');
    if (await isStaffAccessRefusal(res)) {
      let message: string | undefined;
      try { message = (await res.clone().json())?.message; } catch { /* default message */ }
      return { kind: 'blocked', message };
    }
    if (!res.ok) return { kind: 'fallback' };
    const data = await res.json();
    const valid =
      data && Array.isArray(data.orgs) && Array.isArray(data.permissions) &&
      (data.orgId === null || typeof data.orgId === 'string') &&
      typeof data.isSuperAdmin === 'boolean' && typeof data.isPlatformStaff === 'boolean' &&
      data.orgs.every((o: any) => o && typeof o.id === 'string');
    return valid ? { kind: 'ok', data } : { kind: 'fallback' };
  } catch {
    return { kind: 'fallback' };
  }
}

const STAFF_BLOCKED_DEFAULT =
  'Access to customer data is paused because the staff access log could not be written. Nothing was shown or changed. Try again in a moment; if it keeps happening, tell engineering.';

export function OrgProvider({ children }: { children: ReactNode }) {
  // Set when the backend refuses a staff request because the visit could not be logged (fail closed).
  const [staffBlocked, setStaffBlocked] = useState<string | null>(null);
  useEffect(() => {
    const onBlocked = (e: Event) => setStaffBlocked((e as CustomEvent).detail?.message || STAFF_BLOCKED_DEFAULT);
    window.addEventListener(STAFF_ACCESS_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(STAFF_ACCESS_BLOCKED_EVENT, onBlocked);
  }, []);
  const [state, setState] = useState<OrgState>({
    orgId: null,
    role: null,
    isSuperAdmin: false,
    isPlatformStaff: false,
    permissions: [],
    loading: true,
    orgs: [],
    capabilities: DEFAULT_CAPS,
    currency: 'EUR',
  });

  // Switch workspace: persist the choice and reload so every page (and the
  // backend, via the X-Org-Id header apiFetch now sends) re-reads under the new
  // org. A reload is the simplest guarantee that no stale org data lingers.
  const setOrgId = useCallback((id: string) => {
    try { localStorage.setItem(ORG_STORAGE_KEY, id); } catch { /* ignore */ }
    clearAll(); // nothing remembered for the old company survives the switch (the reload also forgets it)
    window.location.reload();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        if (!cancelled) setState({ orgId: null, role: null, isSuperAdmin: false, isPlatformStaff: false, permissions: [], loading: false, orgs: [], capabilities: DEFAULT_CAPS, currency: 'EUR' });
        return;
      }

      const boot = await loadBootstrap();
      if (cancelled) return;
      if (boot.kind === 'blocked') {
        setStaffBlocked(boot.message || STAFF_BLOCKED_DEFAULT);
        setState({ orgId: null, role: null, isSuperAdmin: false, isPlatformStaff: false, permissions: [], loading: false, orgs: [], capabilities: DEFAULT_CAPS, currency: 'EUR' });
        return;
      }
      if (boot.kind === 'ok') {
        const d = boot.data;
        // Keep localStorage in sync (self-heals a stale/removed org id), exactly as the separate path does.
        try {
          if (d.orgId) localStorage.setItem(ORG_STORAGE_KEY, d.orgId);
          else localStorage.removeItem(ORG_STORAGE_KEY);
        } catch { /* ignore */ }
        setState({
          orgId: d.orgId ?? null,
          role: d.role ?? null,
          isSuperAdmin: d.isSuperAdmin,
          isPlatformStaff: d.isPlatformStaff,
          // Fail closed: only an array of strings counts.
          permissions: d.permissions.filter((p: unknown) => typeof p === 'string'),
          loading: false,
          orgs: d.orgs,
          capabilities: d.capabilities ? { ...DEFAULT_CAPS, ...d.capabilities } : DEFAULT_CAPS,
          currency: d.currency || 'EUR',
        });
        return;
      }

      // ALL the user's active memberships (earliest first), plus their platform
      // role. The active workspace is the persisted choice if it's still one of
      // them, otherwise the earliest — so a single-org user is unaffected.
      const [{ data: memberships, error }, { data: profile }] = await Promise.all([
        supabase
          .from('organization_memberships')
          .select('organization_id, role, organizations(name, slug, base_currency)')
          .eq('user_id', userId)
          .eq('is_active', true)
          .order('created_at', { ascending: true }),
        supabase.from('profiles').select('platform_role').eq('id', userId).maybeSingle(),
      ]);
      if (cancelled) return;

      const isSuperAdmin = profile?.platform_role === 'super_admin';
      const isPlatformStaff = profile?.platform_role === 'support' || isSuperAdmin;

      if (error) {
        console.error('[org] membership lookup failed:', error.message);
        setState({ orgId: null, role: null, isSuperAdmin, isPlatformStaff, permissions: [], loading: false, orgs: [], capabilities: DEFAULT_CAPS, currency: 'EUR' });
        return;
      }
      const membershipOrgs: OrgSummary[] = (memberships || []).map((m: any) => {
        const org = Array.isArray(m.organizations) ? m.organizations[0] : m.organizations;
        return { id: m.organization_id, name: org?.name ?? null, slug: org?.slug ?? null, role: m.role, currency: org?.base_currency ?? 'EUR' };
      });
      // Platform staff hold no membership rows at all — every company they can
      // reach comes from this list instead, labelled with a synthetic role (never
      // a real membership role) so the UI can still show something in that column.
      // A staff account that ALSO happens to have a real membership somewhere
      // keeps that membership's real role for that one company.
      let orgs = membershipOrgs;
      if (isPlatformStaff) {
        try {
          const res = await apiFetch('/api/platform/orgs');
          if (res.ok) {
            const data = await res.json();
            const known = new Set(membershipOrgs.map((o) => o.id));
            const staffRole = isSuperAdmin ? 'staff (super-admin)' : 'staff (read-only)';
            const staffOrgs: OrgSummary[] = (data.orgs || [])
              .filter((o: any) => !known.has(o.id))
              .map((o: any) => ({ id: o.id, name: o.name ?? null, slug: o.slug ?? null, role: staffRole, currency: 'EUR' }));
            orgs = [...membershipOrgs, ...staffOrgs];
          }
        } catch { /* keep just the real memberships */ }
      }
      if (cancelled) return;
      // Chosen workspace: persisted choice if still in the list, else earliest.
      let stored: string | null = null;
      try { stored = localStorage.getItem(ORG_STORAGE_KEY); } catch { /* ignore */ }
      const current = orgs.find((o) => o.id === stored) || orgs[0] || null;
      // Keep localStorage in sync (self-heals a stale/removed org id).
      try {
        if (current) localStorage.setItem(ORG_STORAGE_KEY, current.id);
        else localStorage.removeItem(ORG_STORAGE_KEY);
      } catch { /* ignore */ }
      // Composable capabilities for the active org (reconciliation/agent/
      // manufacturing) — from the backend registry. Fail-safe: default to
      // reconciliation-only so the app is never blocked if the call fails.
      let capabilities: Capabilities = DEFAULT_CAPS;
      // What the caller may do in this company. Fail closed: [] on any problem.
      let permissions: string[] = [];
      if (current) {
        try {
          const res = await apiFetch('/api/org/permissions');
          if (await isStaffAccessRefusal(res)) {
            let message: string | undefined;
            try { message = (await res.clone().json())?.message; } catch { /* default message */ }
            if (!cancelled) setStaffBlocked(message || STAFF_BLOCKED_DEFAULT);
          } else if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data?.permissions)) permissions = data.permissions;
          }
        } catch { /* no permissions: no change buttons */ }
      }
      if (current) {
        try {
          const res = await apiFetch('/api/org/capabilities');
          if (res.ok) {
            const data = await res.json();
            if (data?.capabilities) capabilities = { ...DEFAULT_CAPS, ...data.capabilities };
          }
        } catch { /* keep default */ }
      }
      if (cancelled) return;
      // No membership: do NOT fall back to any org — RLS would show nothing anyway,
      // and silently landing in another workspace would be wrong.
      setState({
        orgId: current?.id ?? null,
        role: current?.role ?? null,
        isSuperAdmin,
        isPlatformStaff,
        permissions,
        loading: false,
        orgs,
        capabilities,
        currency: (current as any)?.currency ?? 'EUR',
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Ask again for permissions that did not arrive (see PERMISSION_RETRY_DELAYS_MS). Stops on the first non-empty answer, on the staff-log refusal, when the company
  // changes or the page goes away.
  const [permAttempt, setPermAttempt] = useState(0);
  const retryOrg = state.orgId;
  const needsPermissions = !state.loading && !!retryOrg && state.permissions.length === 0;
  useEffect(() => {
    if (!needsPermissions || permAttempt >= PERMISSION_RETRY_DELAYS_MS.length) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch('/api/org/permissions');
        if (cancelled) return;
        if (await isStaffAccessRefusal(res)) return;
        if (res.ok) {
          const data = await res.json();
          const list: string[] = Array.isArray(data?.permissions) ? data.permissions.filter((p: unknown) => typeof p === 'string') : [];
          if (list.length > 0) {
            if (!cancelled) setState((prev) => (prev.orgId === retryOrg ? { ...prev, permissions: list } : prev));
            return;
          }
        }
      } catch { /* try again, or stay closed */ }
      if (!cancelled) setPermAttempt((a) => a + 1);
    }, PERMISSION_RETRY_DELAYS_MS[permAttempt]);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [needsPermissions, permAttempt, retryOrg]);

  if (state.loading) {
    return <CenterScreen>Loading…</CenterScreen>;
  }

  // A staff visit that could not be logged is refused: show why, never an empty or half-working app.
  if (staffBlocked) {
    return (
      <CenterScreen>
        <div style={{ maxWidth: 460, textAlign: 'center' }} role="alert">
          <div style={{ fontSize: 16, fontWeight: 600, color: C.textDark, marginBottom: 8 }}>
            Customer data is temporarily unavailable
          </div>
          <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 20 }}>{staffBlocked}</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{ padding: '9px 18px', borderRadius: 10, background: C.orange, border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => supabase.auth.signOut()}
              style={{ padding: '9px 18px', borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, color: C.textDark, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Sign out
            </button>
          </div>
        </div>
      </CenterScreen>
    );
  }

  // Logged in but not linked to any workspace (and not Floxir staff, who reach
  // every workspace without a membership). Give a calm explanation and a way
  // out, never a fake/empty app.
  if (!state.orgId && !state.isPlatformStaff) {
    return (
      <CenterScreen>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.textDark, marginBottom: 8 }}>
            Your account isn’t part of a workspace yet
          </div>
          <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 20 }}>
            Ask the person who invited you to finish setting up your access, then sign in again.
          </div>
          <button
            type="button"
            onClick={() => supabase.auth.signOut()}
            style={{
              padding: '9px 18px',
              borderRadius: 10,
              background: C.card,
              border: `1px solid ${C.border}`,
              color: C.textDark,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Sign out
          </button>
        </div>
      </CenterScreen>
    );
  }

  const can = (key: string) => state.permissions.includes(key);
  return <OrgContext.Provider value={{ ...state, setOrgId, can }}>{children}</OrgContext.Provider>;
}

function CenterScreen({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontSize: 14,
        color: C.textMuted,
        background: C.bg,
      }}
    >
      {children}
    </div>
  );
}
